import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Grid, Typography, Box, CardContent, CardHeader, Card, Stack, Divider, Button, Tooltip, FormControl, OutlinedInput } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useNavigate } from 'react-router-dom';
import { Document } from '../../../types/DocumentType';
import axios from '../../../../services/ApiService';
import { Company } from '../../../types/UserTypes';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../../support/LoadingSpinner';
import { BVTConstants } from '../../../../utils/BVTConstants';
import { useDocuments } from '../../../../hooks/useDocuments';
import { Iconify } from '../../../core/iconify';
import CompanyServiceInfo from './CompanyServiceInfo';

function CompanyProfile() {
  const navigate = useNavigate();
  const { saveDocument, getDocumentUrl } = useDocuments();
  const [error, setError] = useState<string>('');
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [companyData, setCompanyData] = useState<Company | null>(null);
  const [logoData, setLogoData] = useState<Document>({
    id: 0,
    fileName: '',
    purpose: '',
    contentFile: null,
    contentBytes: null,
    status: '',
    mimeType: '',
    createdAt: '',
    updatedAt: '',
  });
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isEditingWebLink, setIsEditingWebLink] = useState(false);
  const [webLinkValue, setWebLinkValue] = useState('');
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [contactValue, setContactValue] = useState('');

  useEffect(() => {
    fetchCompanyData();
  }, []);

  useEffect(() => {
    const fetchLogo = async () => {
      if (companyData?.logoId) {
        try {
          const url = await getDocumentUrl(companyData.logoId);
          setLogoUrl(url);
        } catch (error) {
          console.error('Error fetching logo:', error);
        }
      }
    };
    fetchLogo();
  }, [companyData?.logoId]);

  const fetchCompanyData = async () => {
    try {
      setDataLoading(true);
      const response = await axios.get("/getCurrentUserCompany");
      setCompanyData(response.data);
    } catch (error) {
      console.error('Error fetching company data.');
    } finally {
      setDataLoading(false);
    }
  };

  const handleLogoUpdate = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, JPG, or PNG)');
        return;
      }

      if (file.size > 1024 * 1024) { // 1MB limit (1024 * 1024 bytes)
        toast.error('File size should not exceed 1 MB');
        return;
      }

      setLoading(true);
      try {
        const documentData = {
          ...logoData,
          contentFile: file,
          fileName: file.name,
          purpose: BVTConstants.COMPANY_LOGO
        };

        setLogoData(documentData);

        const docId = await saveDocument(documentData);
        
        if (companyData) {
          const updatedCompanyData = {
            ...companyData,
            logoId: docId
          };
          await axios.post("/updateCompanyLogo", updatedCompanyData);
          setCompanyData(updatedCompanyData);
          
          const newLogoUrl = await getDocumentUrl(docId);
          setLogoUrl(newLogoUrl);
          
          toast.success("Company logo updated successfully");
        }
      } catch (error) {
        console.error('Error updating logo');
        toast.error("Failed to update logo");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleWebLinkUpdate = async () => {
    if (!companyData) return;
    
    setLoading(true);
    try {
      const updatedCompanyData = {
        ...companyData,
        webLink: webLinkValue
      };
      await axios.post("/updateCompanyProfile", updatedCompanyData);
      setCompanyData(updatedCompanyData);
      setIsEditingWebLink(false);
      toast.success("Web link updated successfully");
    } catch (error) {
      console.error('Error updating web link');
      toast.error("Failed to update web link");
    } finally {
      setLoading(false);
    }
  };

  const handleContactUpdate = async () => {
    if (!companyData) return;
    
    // Validate phone number
    if (!contactValue.trim()) {
      toast.error("Contact number is required");
      return;
    }
    
    if (!BVTConstants.PHONE_VALIDATION_PATTERN.test(contactValue.trim())) {
      toast.error("Please enter a valid phone number");
      return;
    }
    
    if (contactValue.trim().length < BVTConstants.PHONE_MIN_LENGTH || 
        contactValue.trim().length > BVTConstants.PHONE_MAX_LENGTH) {
      toast.error(`Phone number must be between ${BVTConstants.PHONE_MIN_LENGTH}-${BVTConstants.PHONE_MAX_LENGTH} characters`);
      return;
    }
    
    setLoading(true);
    try {
      const updatedCompanyData = {
        ...companyData,
        contact: contactValue
      };
      await axios.post("/updateCompanyProfile", updatedCompanyData);
      setCompanyData(updatedCompanyData);
      setIsEditingContact(false);
      toast.success("Contact number updated successfully");
    } catch (error) {
      console.error('Error updating contact number');
      toast.error("Failed to update contact number");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Helmet><title>BVT Admin - Companies</title></Helmet>
    {dataLoading ? (
          <LoadingSpinner loading={dataLoading} />
        ) : error ? (
          <Typography variant="h6" color="error" textAlign="center">
            {error}
          </Typography>
        ) : (
        <Stack spacing={2}>
          <Card>
            <CardHeader 
              title={companyData?.companyName}
              subheader="Base Profile"
              // action={
              //   <>
              //     <IconButton 
              //       aria-label="close" 
              //       onClick={() => navigate(-1)}
              //       sx={{ 
              //         border: '1px solid',
              //         borderColor: 'divider',
              //         ml: 1
              //       }}
              //     >
              //       <CloseIcon />
              //     </IconButton>
              //   </>
              // }
              />
            <Divider />
            <CardContent>
              <Box sx={{ maxWidth: '900px' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={10}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Company Name
                        </Typography>
                        <Typography variant="body1">{companyData?.companyName}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Agent ABN
                        </Typography>
                        <Typography variant="body1">{companyData?.agentABN}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Contact First Name
                        </Typography>
                        <Typography variant="body1">{companyData?.firstName}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Contact Last Name
                        </Typography>
                        <Typography variant="body1">{companyData?.lastName}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Email
                        </Typography>
                        <Typography variant="body1">{companyData?.email}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Office Location
                        </Typography>
                        <Typography variant="body1">{companyData?.address}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Contact No.
                          </Typography>
                          {!isEditingContact && (
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                setIsEditingContact(true);
                                setContactValue(companyData?.contact || '');
                              }}
                              sx={{ color: 'warning.main' }}
                            >
                              <Iconify icon="eva:edit-2-fill" />
                            </IconButton>
                          )}
                        </Box>
                        {isEditingContact ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControl fullWidth>
                              <OutlinedInput
                                size="small"
                                value={contactValue}
                                onChange={(e) => {
                                  const value = e.target.value.replace(BVTConstants.PHONE_INPUT_PATTERN, '');
                                  setContactValue(value);
                                }}
                                placeholder="Enter contact number"
                                inputProps={{ maxLength: BVTConstants.PHONE_MAX_LENGTH }}
                              />
                            </FormControl>
                            <IconButton 
                              size="small" 
                              onClick={handleContactUpdate}
                              sx={{ color: 'success.main' }}
                            >
                              <Iconify icon="eva:checkmark-circle-2-fill" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                setIsEditingContact(false);
                                setContactValue(companyData?.contact || '');
                              }}
                              sx={{ color: 'error.main' }}
                            >
                              <Iconify icon="eva:close-circle-fill" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Typography variant="body1">{companyData?.contact}</Typography>
                        )}
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Web Link
                          </Typography>
                          {!isEditingWebLink && (
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                setIsEditingWebLink(true);
                                setWebLinkValue(companyData?.webLink || '');
                              }}
                              sx={{ color: 'warning.main' }}
                            >
                              <Iconify icon="eva:edit-2-fill" />
                            </IconButton>
                          )}
                        </Box>
                        {isEditingWebLink ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControl fullWidth>
                              <OutlinedInput
                                size="small"
                                value={webLinkValue}
                                onChange={(e) => setWebLinkValue(e.target.value)}
                                placeholder="Enter web link"
                                inputProps={{ maxLength: 500 }}
                              />
                            </FormControl>
                            <IconButton 
                              size="small" 
                              onClick={handleWebLinkUpdate}
                              sx={{ color: 'success.main' }}
                            >
                              <Iconify icon="eva:checkmark-circle-2-fill" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                setIsEditingWebLink(false);
                                setWebLinkValue(companyData?.webLink || '');
                              }}
                              sx={{ color: 'error.main' }}
                            >
                              <Iconify icon="eva:close-circle-fill" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Tooltip title={companyData?.webLink || 'N/A'}>
                            <Typography variant="body1">
                              {companyData?.webLink ? (
                                <a 
                                  href={companyData.webLink.startsWith('http') ? companyData.webLink : `https://${companyData.webLink}`}
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ color: 'inherit', textDecoration: 'none' }}
                                >
                                  {companyData.webLink.length > 35 
                                    ? `${companyData.webLink.substring(0, 35)}...` 
                                    : companyData.webLink}
                                </a>
                              ) : (
                                'N/A'
                              )}
                            </Typography>
                          </Tooltip>
                        )}
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Member Since
                        </Typography>
                        <Typography variant="body1">{companyData?.createdAt}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Is Verified
                        </Typography>
                        <Typography variant="body1">{String(companyData?.isVerified) === 'true' ? 'Yes' : 'No'}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Box 
                      sx={{ 
                        width: '100%', 
                        height: 160, 
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      {logoUrl ? (
                        <img 
                          src={logoUrl}
                          alt="Company Logo"
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '100%', 
                            objectFit: 'contain' 
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No Logo Available
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                    >
                      Update Logo
                      <input
                        type="file"
                        hidden
                        accept=".jpg,.jpeg,.png"
                        onChange={handleLogoUpdate}
                      />
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
          <CompanyServiceInfo />
          <LoadingSpinner loading={loading} />
        </Stack>
        )}
    </>
  )
}

export default CompanyProfile